import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	TimeScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Title,
	LineController,
	BarController,
	Decimation,
	Interaction,
} from "chart.js";
import { getRelativePosition } from "chart.js/helpers";
import moment from "moment";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from "chartjs-plugin-annotation";
import dataLabelPlugin from "chartjs-plugin-datalabels";
import "chartjs-adapter-moment";
import { Chart } from "react-chartjs-2";

import "../../styles/StackPlot.css";
import { colors } from "../../styles/colors";
import { ifExists, isNumeric } from "../../util/globals";
import { DataValidationContext } from "./DataValidation";

ChartJS.register(
	LinearScale,
	CategoryScale,
	TimeScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Title,
	LineController,
	BarController,
	zoomPlugin,
	annotationPlugin,
	Decimation,
);

const INTERVALS = {
	hourly: 60 * 60,
	"1-min": 60,
	"5-min": 60 * 5,
	"15-min": 60 * 15,
	daily: 60 * 60 * 24,
	cumulative: 60 * 60,
	"24-hr run": 60 * 60,
};

const Y_AXIS_LABEL_WIDTH = 50;

const STACKPLOT_PAGE_HEIGHT = 1100;
const STACKPLOT_PAGE_WIDTH = 850;
const STACKPLOT_PAGE_MARGIN = 25;
const STACKPLOT_TITLE_HEIGHT = 50;
const STACKPLOT_LABEL_HEIGHT = 15;
const STACKPLOT_LABEL_BOTTOM_MARGIN = -3;
const STACKPLOT_LABEL_EFFECTIVE_HEIGHT =
	STACKPLOT_LABEL_HEIGHT + STACKPLOT_LABEL_BOTTOM_MARGIN;
const STACKPLOT_LABEL_FONT_SIZE = "12px";
const STACKPLOT_X_AXIS_HEIGHT = 40;
const STACKPLOT_FOOTNOTE_HEIGHT = 25;
const STACKPLOT_INNER_PAGE_HEIGHT =
	STACKPLOT_PAGE_HEIGHT - 2 * STACKPLOT_PAGE_MARGIN;
const STACKPLOT_INNER_PAGE_WIDTH =
	STACKPLOT_PAGE_WIDTH - 2 * STACKPLOT_PAGE_MARGIN;
const STACKPLOT_WRAPPER_BORDER_WIDTH = 1;

const STACKPLOT_AXIS_FONT = {
	size: 10,
};

const STACKPLOT_AXIS_MAJOR_FONT = {
	...STACKPLOT_AXIS_FONT,
	weight: "bold",
};

Interaction.modes.myCustomMode = function (
	chart,
	e,
	options,
	useFinalPosition,
) {
	const position = getRelativePosition(e, chart);

	const items = [];
	const itemPerDataset = {};

	const originalLog = console.log;

	try {
		console.log = () => {};

		Interaction.evaluateInteractionItems(
			chart,
			"x",
			position,
			(element, datasetIndex, index) => {
				if (element.inXRange(position.x, useFinalPosition)) {
					itemPerDataset[datasetIndex] = {
						element,
						datasetIndex,
						index,
					};
				}
			},
		);
	} finally {
		console.log = originalLog;
	}

	for (const datasetIndex of Object.keys(itemPerDataset).sort(
		(a, b) => Number(a) - Number(b),
	)) {
		items.push(itemPerDataset[datasetIndex]);
	}

	return items;
};

const getOrCreateTooltip = (chart) => {
	let tooltipEl = chart.canvas.parentNode.querySelector(
		".validation-tooltip",
	);

	if (!tooltipEl) {
		tooltipEl = document.createElement("div");
		tooltipEl.classList.add("validation-tooltip");

		const table = document.createElement("table");
		table.style.margin = "0px";

		tooltipEl.appendChild(table);
		chart.canvas.parentNode.appendChild(tooltipEl);
	}

	return tooltipEl;
};

const externalTooltipHandler = (context) => {
	// Tooltip Element
	const { chart, tooltip } = context;
	const tooltipEl = getOrCreateTooltip(chart);

	// Hide if no tooltip
	if (tooltip.opacity === 0) {
		tooltipEl.style.opacity = 0;
		return;
	}

	tooltipEl.style.transform = `translate(${tooltip.caretX > chart.width / 2 ? -100 : 0}%, 0)`;

	// Set Text
	if (tooltip.body) {
		const titleLines = tooltip.title || [];
		const bodyLines = tooltip.body.map((b) => b.lines);

		const tableHead = document.createElement("thead");

		titleLines.forEach((title) => {
			const tr = document.createElement("tr");
			tr.style.borderWidth = 0;
			tr.style.color = "white";
			tr.style.fontSize = "0.75rem";

			const th = document.createElement("th");
			th.style.borderWidth = 0;
			th.style.padding = "0 2px";
			th.colSpan = 3;
			const text = document.createTextNode(title);

			th.appendChild(text);
			tr.appendChild(th);
			tableHead.appendChild(tr);
		});

		const tr2 = document.createElement("tr");
		tr2.style.borderWidth = 0;
		tr2.style.color = "white";
		tr2.style.fontSize = "0.75rem";

		const th1 = document.createElement("th");
		th1.style.borderWidth = 0;
		th1.style.padding = "0 2px";
		const thText1 = document.createTextNode("Parameter");
		th1.appendChild(thText1);

		const th2 = document.createElement("th");
		th2.style.borderWidth = 0;
		th2.style.padding = "0 2px";
		const thText2 = document.createTextNode("Raw");
		th2.appendChild(thText2);

		const th3 = document.createElement("th");
		th3.style.borderWidth = 0;
		th3.style.padding = "0 2px";
		const thText3 = document.createTextNode("Val");
		th3.appendChild(thText3);

		tr2.appendChild(th1);
		tr2.appendChild(th2);
		tr2.appendChild(th3);

		tableHead.appendChild(tr2);

		const pars = {};
		bodyLines.forEach((body, i) => {
			const line = body[0]?.split("/");
			if (!line?.length) return;
			let key;
			if (line.length === 7) {
				key = `${line[0]} ${line[1]} ${line[2]}`;
				if (!(key in pars)) {
					pars[key] = { RAW: null, VAL: null };
				}
				pars[key][line[3]] = { value: line[4], flag: line[6] };
				pars[key].unit = line[5];
			} else {
				key = line[0];
				if (!(key in pars)) {
					pars[key] = { RAW: null, VAL: null };
				}
				pars[key][line[1]] = { value: line[2], flag: line[4] };
				pars[key].unit = line[3];
			}
			const colors = tooltip.labelColors[i];
			pars[key].borderColor = colors.borderColor;
			pars[key].backgroundColor = colors.backgroundColor;
		});

		const tableBody = document.createElement("tbody");
		Object.entries(pars).forEach(([key, par], i) => {
			//   const colors = tooltip.labelColors[i];

			const span = document.createElement("span");
			span.style.background = par.backgroundColor;
			span.style.borderColor = par.borderColor;
			span.style.borderWidth = "2px";
			span.style.borderStyle = "solid";
			span.style.marginRight = "10px";
			span.style.height = "10px";
			span.style.width = "10px";
			span.style.display = "inline-block";

			const tr = document.createElement("tr");
			tr.style.backgroundColor = "inherit";
			tr.style.borderWidth = 0;
			tr.style.color = "white";
			tr.style.fontSize = "0.75rem";

			const td1 = document.createElement("td");
			td1.style.borderWidth = 0;
			td1.style.padding = "0 2px";

			const td2 = document.createElement("td");
			td2.style.borderWidth = 0;
			td2.style.padding = "0 2px";

			const td3 = document.createElement("td");
			td3.style.borderWidth = 0;
			td3.style.padding = "0 2px";

			const text1 = document.createTextNode(`${key} (${par.unit})`);
			const raw = par["RAW"];
			const text2 = document.createTextNode(
				raw
					? raw.value + (raw.flag?.length ? ` (${raw.flag})` : "")
					: null,
			);
			const val = par["VAL"];
			const text3 = document.createTextNode(
				val
					? val.value + (val.flag?.length ? ` (${val.flag})` : "")
					: null,
			);

			td1.appendChild(span);
			td1.appendChild(text1);
			td2.appendChild(text2);
			td3.appendChild(text3);
			tr.appendChild(td1);
			tr.appendChild(td2);
			tr.appendChild(td3);
			tableBody.appendChild(tr);
		});

		const tableRoot = tooltipEl.querySelector("table");

		const tooltipKey = JSON.stringify({
			title: titleLines,
			pars,
		});

		if (tooltipEl.dataset.tooltipKey !== tooltipKey) {
			tooltipEl.dataset.tooltipKey = tooltipKey;

			while (tableRoot?.firstChild) {
				tableRoot.firstChild.remove();
			}

			tableRoot.appendChild(tableHead);
			tableRoot.appendChild(tableBody);
		}
	}

	const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

	// Display, position, and set styles for font
	tooltipEl.style.opacity = 1;
	tooltipEl.style.left = positionX + tooltip.caretX + "px";
	tooltipEl.style.top = chart.height + "px";
	tooltipEl.style.font = tooltip.options.bodyFont.string;
	tooltipEl.style.color = "white";
	tooltipEl.style.padding =
		tooltip.options.padding + "px " + tooltip.options.padding + "px";
};

const defaultPlotOptions = {
	responsive: true,
	animation: false,
	devicePixelRatio: 2,
	interaction: {
		mode: "myCustomMode",
		intersect: false,
	},
	layout: {
		padding: {
			top: 0,
			bottom: 4,
			left: 0,
			right: Y_AXIS_LABEL_WIDTH,
		},
	},
	datasets: {
		line: {
			spanGaps: false,
		},
	},
	plugins: {
		legend: {
			display: false,
		},
		tooltip: {
			enabled: false,
			position: "average",
		},
		decimation: {
			enabled: true,
		},
		zoom: {
			zoom: {
				wheel: {
					enabled: true,
				},
				pinch: {
					enabled: true,
				},
				mode: "xy",
			},
			pan: {
				enabled: true,
				mode: "xy",
			},
			limits: {
				x: {
					min: "original",
					max: "original",
					minRange: 60 * 60 * 1000,
				},
			},
		},
		annotation: {
			annotations: { cursor1: null, cursor2: null },
		},
	},
	scales: {
		x: {
			type: "time",
			time: {
				minUnit: "hour",
			},
			ticks: {
				display: false,
			},
			offset: false,
			grid: {
				offset: false,
				color: colors["ars-neutral-750"],
			},
		},
		y: {
			type: "linear",
			suggestedMin: 0,
			suggestedMax: 1,
			ticks: {
				font: STACKPLOT_AXIS_FONT,
			},
			grid: {
				color: colors["ars-neutral-750"],
			},
		},
	},
	elements: {
		bar: {
			borderWidth: 0,
		},
		point: {
			hitRadius: 10,
		},
	},
	events: [
		"mousemove",
		"mouseout",
		"click",
		"touchstart",
		"touchmove",
		"dblclick",
		"keydown",
		"blur",
	],
	maintainAspectRatio: false,
	parsing: false,
};

const xAxisOptions = {
	responsive: true,
	animation: false,
	devicePixelRatio: 2,
	layout: {
		padding: {
			top: -100,
			bottom: 0,
			left: Y_AXIS_LABEL_WIDTH,
			right: Y_AXIS_LABEL_WIDTH,
		},
	},
	scales: {
		x: {
			type: "time",
			position: "bottom",
			time: {
				unit: "hour",
				displayFormats: {
					minute: "HH:mm",
					hour: "HH:mm",
					day: "MMM D",
					year: "YYYY",
				},
			},
			ticks: {
				major: {
					enabled: true,
				},
				font: (context) => {
					if (context.tick.major) return STACKPLOT_AXIS_MAJOR_FONT;
					else return STACKPLOT_AXIS_FONT;
				},
				minRotation: 90,
				maxRotation: 90,
				callback: (value, index, ticks) => {
					const tick = ticks[index];
					const mmnt = moment(tick.value);
					if (tick.major) {
						if (mmnt.format("MM/DD HH:mm") === "01/01 00:00") {
							return mmnt.format("YYYY");
						} else {
							return mmnt.format("MM/DD");
						}
					} else {
						return mmnt.format("HH:mm");
					}
				},
			},
			grid: {
				display: true,
				color: colors["ars-neutral-750"],
			},
			border: {
				display: false,
			},
		},
		y: {
			display: false,
		},
	},
	plugins: {
		zoom: {
			zoom: {
				mode: "x",
			},
			pan: {
				enabled: true,
				mode: "x",
			},
			limits: {
				x: {
					min: "original",
					max: "original",
					minRange: 60 * 60 * 1000,
				},
			},
		},
	},
	maintainAspectRatio: false,
};

const backgroundColorPlugin = {
	id: "customCanvasBackgroundColor",
	beforeDraw: (chart, args, options) => {
		const { ctx } = chart;
		ctx.save();
		ctx.globalCompositeOperation = "destination-over";
		ctx.fillStyle = options.color || "transparent";
		ctx.fillRect(0, 0, chart.width, chart.height);
		ctx.restore();
	},
};

const ValidationPlotContext = createContext(null);

const GREEN_CODES = ["V", "VA", "VZ"];
const YELLOW_CODES = ["MT", "ZS"];
const RED_CODES = ["PF", "IM"];

function updateRange(scale, { min, max }, limits, zoom = false) {
	const { id, options: scaleOpts } = scale;

	const scaleLimits = limits[id] || {};
	const { minRange = 0 } = scaleLimits;

	const minLimit =
		!scale.isHorizontal() || scaleLimits.min === undefined
			? -Infinity
			: scaleLimits.min;

	const maxLimit =
		!scale.isHorizontal() || scaleLimits.max === undefined
			? Infinity
			: scaleLimits.max;

	const range = zoom ? Math.max(max - min, minRange) : scale.max - scale.min;

	const offset = (range - max + min) / 2;

	min -= offset;
	max += offset;

	if (min < minLimit) {
		min = minLimit;
		max = Math.min(minLimit + range, maxLimit);
	} else if (max > maxLimit) {
		max = maxLimit;
		min = Math.max(maxLimit - range, minLimit);
	}

	scaleOpts.min = min;
	scaleOpts.max = max;

	return scale.parse(min) !== scale.min || scale.parse(max) !== scale.max;
}

zoomPlugin.panFunctions.default = (scale, delta) => {
	const { min: prevStart, max: prevEnd, id } = scale;

	if (scale.chart.isCalPlot && id === "y1") {
		return false;
	}

	const limits = scale.chart.originalScales;

	if (!limits) {
		return false;
	}

	const newMin = scale.getValueForPixel(
		scale.getPixelForValue(prevStart) - delta,
	);

	const newMax = scale.getValueForPixel(
		scale.getPixelForValue(prevEnd) - delta,
	);

	return updateRange(
		scale,
		{
			min: newMin,
			max: newMax,
		},
		limits,
		false,
	);
};

function ValidationPlot({
	data,
	config,
	plugins = [],
	dateRange = null,
	stackPlotRef = null,
	printable = false,
	callbacks = null,
	highlighted,
	setHighlighted,
	scrollTableOnCursor,
	onClearCursors,
	autoYAxis,
	fixedYScales,
	zoomLevel = 100,
	isPopout = false,
}) {
	const [chartData, setChartData] = useState({});
	const chartRefs = useRef(new Map());
	const chartZooms = useRef(new Map([["x", null]]));
	const cursors = useRef(new Map());
	const originalScales = useRef(new Map());
	const [showCalDataLabels, setCalDataLabels] = useState(true);

	const { isHighlighted, parCodeIntervalMap } = useContext(
		DataValidationContext,
	);

	const timestampLabels = useMemo(() => {
		if (!data?._timestamps?.length) {
			return [];
		}

		return data._timestamps.map((timestamp) =>
			moment.unix(timestamp).seconds(0).format("YYYY-MM-DDTHH:mm"),
		);
	}, [data?._timestamps]);

	useEffect(() => {
		const element = stackPlotRef?.current;

		if (!element) {
			return;
		}

		const handleWheelEvent = (event) => {
			const target = event.target;

			if (event.ctrlKey && target.tagName !== "CANVAS") {
				event.preventDefault();
			}
		};

		element.addEventListener("wheel", handleWheelEvent, {
			passive: false,
		});

		return () => {
			element.removeEventListener("wheel", handleWheelEvent);
		};
	}, [stackPlotRef]);

	useEffect(() => {
		if (dateRange?.min && dateRange?.max) {
			zoomAll(
				moment(dateRange.min, "YYYY-MM-DDTHH:mm").valueOf(),
				moment(dateRange.max, "YYYY-MM-DDTHH:mm").valueOf(),
			);
		}
	}, [dateRange]);

	useEffect(() => {
		chartZooms.current.clear();
		cursors.current.clear();
		originalScales.current.clear();
	}, [config]);

	useEffect(() => {
		if (!config?.charts || !data?._timestamps) return;

		let labels = timestampLabels;
		const newChartData = {};
		config.charts.forEach((chartConfig, index) => {
			const datasets = [];

			if (chartConfig.parameters?.length) {
				if (isCalPlot()) {
					let isZero = false;
					let hasValid = false;
					for (let parameter of chartConfig.parameters) {
						const calPar = [
							parameter.parCode,
							parameter.calType,
							parameter.valType,
						];
						const valCalPar = [
							parameter.parCode + "_VAL",
							parameter.calType,
							parameter.valType,
						];
						if (
							(chartConfig.hasRightSide ||
								parameter.side === "L") &&
							data[calPar]
						) {
							if (parameter.calType.match(/.*Zero/))
								isZero = true;
							datasets.push(
								generateCalDataset(
									data[calPar],
									parameter.parCode,
									parameter.calType,
									parameter.valType,
									parameter.unit,
									parameter.type,
									parameter.color +
										(data[valCalPar] ? "66" : ""),
									parameter.symbol,
									parameter.rawPrec,
									data._timestamps,
									parameter.side === "R" ? "y1" : "y",
								),
							);

							if (data[valCalPar]) {
								if (
									Object.entries(data[valCalPar]).some(
										([key, [value, flag]]) =>
											value !== null,
									)
								) {
									hasValid = true;
									datasets.push(
										generateCalDataset(
											data[valCalPar],
											parameter.parCode + "_VAL",
											parameter.calType,
											parameter.valType,
											parameter.unit,
											parameter.type,
											parameter.color,
											parameter.symbol,
											parameter.rawPrec,
											data._timestamps,
											parameter.side === "R" ? "y1" : "y",
											1,
										),
									);
								}
							}
						}
					}
					const diffDataset = isZero
						? generateDiffDataset(hasValid, chartConfig.parameters)
						: generatePercentDiffDataset(
								hasValid,
								chartConfig.parameters,
							);

					if (diffDataset) {
						datasets.push(diffDataset);
					}
				} else {
					for (let parameter of chartConfig.parameters) {
						if (
							(chartConfig.hasRightSide ||
								parameter.side === "L") &&
							data[parameter.parCode]
						) {
							datasets.push(
								generateDataset(
									data[parameter.parCode],
									parameter.parCode,
									parameter.unit,
									parameter.type,
									parameter.color +
										(data[parameter.parCode + "_VAL"]
											? "66"
											: ""),
									parameter.symbol,
									parameter.rawPrec,
									data._startTime,
									data._endTime,
									parameter.side === "R" ? "y1" : "y",
								),
							);

							if (data[parameter.parCode + "_VAL"]) {
								const lineExtensions = [];
								datasets.push(
									generateDataset(
										data[parameter.parCode + "_VAL"],
										parameter.parCode + "_VAL",
										parameter.unit,
										parameter.type,
										parameter.color,
										parameter.symbol,
										parameter.rawPrec,
										data._startTime,
										data._endTime,
										parameter.side === "R" ? "y1" : "y",
										1,
										lineExtensions,
									),
								);
								if (parameter.type !== "bar") {
									lineExtensions.reverse();
									datasets.push(
										generateExtensionsDataset(
											lineExtensions,
											parameter.color,
											parameter.side === "R" ? "y1" : "y",
										),
									);
								}
							}
						}
					}
				}
			}

			newChartData[index] = { datasets, labels };
		});
		setChartData(newChartData);
	}, [data, config, showCalDataLabels, parCodeIntervalMap]);

	useEffect(() => {
		originalScales.current.clear();
		chartZooms.current.clear();
	}, [autoYAxis, fixedYScales]);

	const generatePages = () => {
		if (!(config?.charts && data?._timestamps)) {
			return null;
		}

		const pages = [];

		const chartSpacing =
			(config.chartSpacing || 20) -
			2 * STACKPLOT_WRAPPER_BORDER_WIDTH -
			STACKPLOT_LABEL_EFFECTIVE_HEIGHT;

		const labels = timestampLabels;

		let plots = [];
		let remainingSpace =
			STACKPLOT_INNER_PAGE_HEIGHT -
			(STACKPLOT_TITLE_HEIGHT +
				STACKPLOT_X_AXIS_HEIGHT +
				(config.footnote ? STACKPLOT_FOOTNOTE_HEIGHT : 0));

		let xIndex = 1;

		config.charts.forEach((chartConfig, index) => {
			let chartHighlights = {};

			if (chartConfig.parameters?.length) {
				if (isCalPlot()) {
					const calPars = [];

					for (const parameter of chartConfig.parameters) {
						const calPar = [
							parameter.parCode,
							parameter.calType,
							parameter.valType,
						];

						if (
							(chartConfig.hasRightSide ||
								parameter.side === "L") &&
							data[calPar]
						) {
							calPars.push(calPar);
						}
					}

					chartHighlights = getAnnotationsFromHighlighted(calPars);
				} else {
					const parCodes = [];

					for (const parameter of chartConfig.parameters) {
						if (
							(chartConfig.hasRightSide ||
								parameter.side === "L") &&
							data[parameter.parCode]
						) {
							parCodes.push(parameter.parCode);
						}
					}

					chartHighlights = getAnnotationsFromHighlighted(parCodes);
				}
			}

			if (remainingSpace < chartConfig.chartHeight + chartSpacing) {
				plots.push(
					<StackPlotXAxis
						key={`x-axis-${xIndex}`}
						data={{
							datasets: [],
							labels,
						}}
						xIndex={xIndex}
						bufferSize={remainingSpace}
					/>,
				);

				pages.push(plots);
				plots = [];

				remainingSpace =
					STACKPLOT_INNER_PAGE_HEIGHT -
					(STACKPLOT_X_AXIS_HEIGHT +
						(config.footnote ? STACKPLOT_FOOTNOTE_HEIGHT : 0));

				xIndex++;
			}

			plots.push(
				<StackPlotChart
					key={`chart-${index}`}
					config={chartConfig}
					data={getChartData(index)}
					options={defaultPlotOptions}
					chartIndex={index}
					chartSpacing={chartSpacing}
					popoverCallback={
						callbacks?.[
							isPopout ? "openPopoverPopout" : "openPopover"
						] || null
					}
					chartHighlights={chartHighlights}
					autoYAxis={autoYAxis}
					fixedYScales={fixedYScales}
				/>,
			);

			remainingSpace -= chartConfig.chartHeight + config.chartSpacing;
		});

		plots.push(
			<StackPlotXAxis
				key={`x-axis-${xIndex}`}
				data={{
					datasets: [],
					labels,
				}}
				xIndex={xIndex}
				bufferSize={remainingSpace}
			/>,
		);

		pages.push(plots);

		return pages.map((pagePlots, index) => (
			<StackPlotPage
				key={`page-${index}`}
				title={config.fullTitle}
				footnote={config.footnote}
				pageNumber={index + 1}
				charts={pagePlots}
				numPages={pages.length}
			/>
		));
	};

	const getChartData = (index) => {
		return chartData?.[index] || { datasets: [], labels: [] };
	};

	const getAnnotationsFromCalHighlighted = (rowKeys) => {
		if (!highlighted || !data || !rowKeys) return {};

		const highlights = {};
		const highlightedTimestamps = new Set();
		const interval = 60 * 60 * 24;

		if (Object.keys(highlighted).length) {
			for (const timestamp of Object.keys(highlighted)) {
				if (
					rowKeys.some((rowKey) => isHighlighted(rowKey, timestamp))
				) {
					highlightedTimestamps.add(
						moment(timestamp)
							.hour(0)
							.minute(0)
							.second(0)
							.millisecond(0)
							.unix(),
					);
				}
			}
		}

		const highlightedTimestampsSorted = Array.from(
			highlightedTimestamps,
		).sort((a, b) => a - b);

		for (let i = 0; i < highlightedTimestampsSorted.length; i++) {
			const timestamp = highlightedTimestampsSorted[i];

			highlights["highlight-" + i] = {
				type: "box",
				xMin: 1000 * timestamp,
				xMax: 1000 * (timestamp + interval),
				yMin: -Infinity,
				yMax: Infinity,
				borderWidth: 0,
				backgroundColor: colors["ars-blue-600"] + "40",
			};
		}

		return highlights;
	};

	const getAnnotationsFromHighlighted = (rowKeys) => {
		if (isCalPlot()) return getAnnotationsFromCalHighlighted(rowKeys);
		if (!highlighted || !data || !rowKeys) return {};
		const highlightedRanges = [];
		const highlights = {};
		if (Object.keys(highlighted).length) {
			const highlightedPars = new Set();
			for (let [key, pars] of Object.entries(highlighted)) {
				for (let par of pars) highlightedPars.add(par);
			}
			let interval = 60;
			for (let rowKey of highlightedPars) {
				interval = Math.max(interval, getIntervalForParCode(rowKey));
			}
			const highlightedTimestamps = new Set();
			const filteredTs = Object.keys(highlighted).filter(
				(ts) => moment(ts).unix() % interval === 0,
			);
			for (let timestamp of filteredTs) {
				if (
					rowKeys.some((rowKey) => isHighlighted(rowKey, timestamp))
				) {
					highlightedTimestamps.add(moment(timestamp).unix());
				}
			}

			if (highlightedTimestamps.size) {
				const highlightedTimestampsSorted = Array.from(
					highlightedTimestamps,
				);
				highlightedTimestampsSorted.sort((a, b) => a - b);
				let l = 0,
					r;
				for (let i = 1; i < highlightedTimestampsSorted.length; i++) {
					if (
						highlightedTimestampsSorted[i] -
							highlightedTimestampsSorted[i - 1] !==
						interval
					) {
						r = i - 1;
						highlightedRanges.push([
							highlightedTimestampsSorted[l],
							highlightedTimestampsSorted[r] + interval,
						]);
						l = i;
					}
				}
				r = highlightedTimestampsSorted.length - 1;
				highlightedRanges.push([
					highlightedTimestampsSorted[l],
					highlightedTimestampsSorted[r] + interval,
				]);

				for (let i = 0; i < highlightedRanges.length; i++) {
					const range = highlightedRanges[i];
					if (range[0] == range[1]) {
						// 1 data point wide
						highlights["highlight-" + i] = {
							type: "box",
							xMin: 1000 * (range[0] - interval / 4),
							xMax: 1000 * (range[1] + interval / 4),
							yMin: -Infinity,
							yMax: Infinity,
							borderWidth: 0,
							backgroundColor: colors["ars-blue-600"] + "40",
						};
					} else {
						highlights["highlight-" + i] = {
							type: "box",
							xMin: 1000 * range[0],
							xMax: 1000 * range[1],
							yMin: -Infinity,
							yMax: Infinity,
							borderWidth: 0,
							backgroundColor: colors["ars-blue-600"] + "40",
						};
					}
				}
			}
		}

		return highlights;
	};

	const clearCursors = () => {
		cursors.current.clear();
		for (const chartId of chartRefs.current.keys()) {
			if (isNaN(chartId)) {
				continue;
			} else {
				const chart = chartRefs.current.get(chartId)?.current;
				if (!chart) {
					continue;
				}
				const annotations =
					chart.options.plugins.annotation.annotations;
				annotations.cursor1 = undefined;
				annotations.cursor2 = undefined;
				for (let key in annotations) {
					if (key.match(/highlight-.*/)) {
						annotations[key] = undefined;
					}
				}
				chart.update("none");
			}
		}
		if (onClearCursors) {
			onClearCursors();
		}
	};

	const moveCursor = (chartIndex, cursorId, xPos, xIndex) => {
		if (isCalPlot()) {
			let ts = moment(xPos).hour(0).minute(0);
			cursors.current.set(cursorId, {
				chartIndex: chartIndex,
				pos: ts.valueOf(),
				index: xIndex,
			});
		} else {
			cursors.current.set(cursorId, {
				chartIndex: chartIndex,
				pos: xPos,
				index: xIndex,
			});
		}
		if (isCalPlot()) {
			const parCode = chartRefs.current
				.get(chartIndex)
				.current.data.datasets[0].label.split(" ")[0];
			scrollTableOnCursor(xPos, parCode, xIndex, chartIndex);
		} else {
			const parCode =
				chartRefs.current.get(chartIndex).current.data.datasets[0]
					.label;
			scrollTableOnCursor(xPos, parCode, xIndex, chartIndex);
		}

		updateHighlightsWithNewCursors();
	};

	const stepCursor = (chartIndex, cursorId, direction) => {
		const c1 = cursors.current.get("cursor1");
		const c2 = cursors.current.get("cursor2");
		const cursor = cursorId === "cursor1" ? c1 : c2;
		if (!cursor) {
			return;
		}
		if (c1 && c2) {
			const startIndex = Math.min(c1.chartIndex, c2.chartIndex);
			const endIndex = Math.max(c1.chartIndex, c2.chartIndex);
			if (chartIndex < startIndex || chartIndex > endIndex) {
				return;
			}
		} else if (c1) {
			if (chartIndex !== c1.chartIndex) {
				return;
			}
		} else if (c2) {
			if (chartIndex !== c2.chartIndex) {
				return;
			}
		} else {
			return;
		}

		const targetChartIndex = cursor.chartIndex;
		const chart = chartRefs.current.get(targetChartIndex)?.current;
		if (!chart) return;
		const timestamps = chart.scales.x.getDataTimestamps();
		if (!timestamps) return;
		if (cursor) {
			const index = Number(cursor.index);

			if (direction === 1) {
				if (index >= timestamps.length - 1) {
					return;
				}

				moveCursor(
					targetChartIndex,
					cursorId,
					timestamps[index + 1],
					index + 1,
				);
			} else if (direction === -1) {
				if (index <= 0) {
					return;
				}

				moveCursor(
					targetChartIndex,
					cursorId,
					timestamps[index - 1],
					index - 1,
				);
			}
		}
	};

	const updateHighlightsWithNewCursors = () => {
		if (!data?._timestamps) return;
		const c1 = cursors.current.get("cursor1");
		const c2 = cursors.current.get("cursor2");
		const newHighlighted = {};
		if (c1 && c2) {
			const leftC = c1.pos <= c2.pos ? c1 : c2;
			const rightC = c1.pos > c2.pos ? c1 : c2;
			const topC = c1.chartIndex <= c2.chartIndex ? c1 : c2;
			const bottomC = c1.chartIndex > c2.chartIndex ? c1 : c2;
			const startIndex = topC.chartIndex;
			const endIndex = bottomC.chartIndex;

			let interval = isCalPlot() ? 60 * 60 * 24 : 60;
			if (!isCalPlot()) {
				for (const chartId of chartRefs.current.keys()) {
					if (isNaN(chartId)) {
						continue;
					} else {
						const chart = chartRefs.current.get(chartId)?.current;
						if (!chart) {
							continue;
						}
						if (chartId >= startIndex && chartId <= endIndex) {
							for (let dataset of chart.data.datasets) {
								interval = Math.max(
									interval,
									getIntervalForParCode(dataset.label),
								);
							}
						}
					}
				}
			}

			const left = leftC.pos;
			let right = rightC.pos + interval * 1000;
			right -= right % (interval * 1000);
			for (const chartId of chartRefs.current.keys()) {
				if (isNaN(chartId)) {
					continue;
				} else {
					const chart = chartRefs.current.get(chartId)?.current;
					if (!chart) {
						continue;
					}
					if (chartId >= startIndex && chartId <= endIndex) {
						for (let ts of chart.scales.x.getDataTimestamps()) {
							if (ts < left) continue;
							else if (ts >= right) break;
							else {
								let formattedTs = moment
									.utc(ts)
									.format("YYYY-MM-DDTHH:mm:ss.SSSZ");
								newHighlighted[formattedTs] = (
									newHighlighted[formattedTs] || []
								).concat(
									chart.data.datasets.map((dataset) => {
										if (dataset.calType) {
											const {
												parCode,
												calType,
												valType,
											} = dataset;
											return [parCode, calType, valType];
										} else {
											return dataset.label;
										}
									}),
								);
							}
						}
					}
				}
			}
		} else if (c1 || c2) {
			const c = c1 || c2;
			let interval = 60;
			for (const chartId of chartRefs.current.keys()) {
				if (chartId === c.chartIndex) {
					const chart = chartRefs.current.get(chartId)?.current;
					if (!chart) {
						break;
					}
					let interval = 60;
					if (!isCalPlot()) {
						for (let dataset of chart.data.datasets) {
							interval = Math.max(
								interval,
								getIntervalForParCode(dataset.label),
							);
						}
					}
					const left = c.pos;
					const right =
						c.pos + (isCalPlot() ? 24 * 60 * 60 : interval) * 1000;
					for (let ts of chart.scales.x.getDataTimestamps()) {
						if (ts < left) continue;
						else if (ts >= right) break;
						else {
							let formattedTs = moment
								.utc(ts)
								.format("YYYY-MM-DDTHH:mm:ss.SSSZ");
							newHighlighted[formattedTs] = (
								newHighlighted[formattedTs] || []
							).concat(
								chart.data.datasets.map((dataset) => {
									if (dataset.calType) {
										const { parCode, calType, valType } =
											dataset;
										return [parCode, calType, valType];
									} else {
										return dataset.label;
									}
								}),
							);
						}
					}
					break;
				}
			}
		}

		setHighlighted(newHighlighted);
	};

	const zoomAll = (xMin, xMax) => {
		zoomOthers(null, xMin, xMax);
	};

	const zoomOthers = (chartIndex, xMin, xMax) => {
		chartZooms.current.set("x", { min: xMin, max: xMax });
		for (let index of chartRefs.current.keys()) {
			if (index === chartIndex) {
				continue;
			}
			const chart = chartRefs.current.get(index)?.current || null;
			if (chart) {
				const chartOriginalScales = originalScales.current.get(index);
				if (chartOriginalScales && !chartOriginalScales["x"]) {
					chartOriginalScales["x"] = {
						min: chart.scales.x.min,
						max: chart.scales.x.max,
					};
					chart.originalScales = {
						...chart.originalScales,
						x: {
							min: chart.scales.x.min,
							max: chart.scales.x.max,
						},
					};
				}
				chart.zoomScale("x", { min: xMin, max: xMax });
			} else {
				chartRefs.current.delete(index);
			}
		}
	};

	const getDatasetType = (type) => {
		if (type === "bar") return type;
		else return "line";
	};

	const getDatasetLineWidth = (type) => {
		return type === "bar" || type === "point" ? 0 : 0.5;
	};

	const getDatasetBackgroundColor = (type, color) => {
		return type === "bar" ? "#" + color : "transparent";
	};

	const datasetHasPoints = (type) => {
		if (type === "point" || type === "line-point") return true;
		else return false;
	};

	const getDatasetSymbol = (symbol) => {
		if (!symbol) return "circle";
		switch (symbol.toUpperCase()) {
			case "X":
				return "crossRot";
			case "T":
				return "triangle";
			default:
				return "circle";
		}
	};

	const getIntervalForParCode = (parCode) => {
		if (!parCode) return 60;
		parCode = parCode.replace(/_VAL/, "");
		const interval = parCodeIntervalMap?.get(parCode) || "hourly";
		return INTERVALS[interval];
	};

	const generateDataset = (
		parData,
		parCode,
		parUnit,
		type,
		color,
		symbol,
		rawPrec,
		beginTime,
		endTime,
		yAxisId = "y",
		lineWidth,
		lineExtensions = null,
	) => {
		const interval = getIntervalForParCode(parCode);

		const lineData = [];
		const lineFlags = [];

		for (
			let timestamp = beginTime;
			timestamp <= endTime;
			timestamp += interval
		) {
			const parValue = parData[timestamp];

			lineData.push({
				x: timestamp * 1000,
				y: parValue ? parValue[0] : null,
			});

			lineFlags.push(parValue ? parValue[1] : null);
		}

		for (let i = lineData.length - 1; i > 0; i--) {
			const point = lineData[i];
			const nextPoint = lineData[i - 1];
			if (
				(point.y === -999 || point.y === null) &&
				nextPoint.y !== -999 &&
				nextPoint.y !== null
			) {
				if (lineExtensions) {
					lineExtensions.push(
						{ x: point.x, y: null },
						{ x: point.x, y: nextPoint.y },
						nextPoint,
					);
				}
			}
		}

		const dataset = {
			type: getDatasetType(type),
			label: parCode,
			borderColor: "#" + color,
			borderWidth: lineWidth || getDatasetLineWidth(type),
			backgroundColor: getDatasetBackgroundColor(type, color),
			fill: false,
			data: lineData,
			flags: lineFlags,
			xAxisID: "x",
			yAxisID: yAxisId,
			tension: 0,
			pointRadius: datasetHasPoints(type) ? 1 : 0,
			pointStyle: datasetHasPoints(type)
				? getDatasetSymbol(symbol)
				: null,
			pointBorderWidth: 1.2,
			pointBackgroundColor: getDatasetBackgroundColor(type, color),
			categoryPercentage: 0.9,
			barPercentage: 0.9,
			min: getMin(lineData.map((point) => point.y)),
			max: getMax(lineData.map((point) => point.y)),

			tooltip: {
				callbacks: {
					label: (tooltipItem) => {
						const pCode = parCode.replace(/_VAL/, "");
						return `${pCode}/${parCode.match(/.*_VAL/) ? "VAL" : "RAW"}/${Number(
							tooltipItem.formattedValue,
						).toFixed(
							rawPrec,
						)}/${parUnit}/${ifExists(lineFlags[tooltipItem.dataIndex]?.y, "")}`;
					},
				},
			},
		};
		return dataset;
	};

	const generateExtensionsDataset = (
		lineExtensions,
		color,
		yAxisId = "y",
	) => {
		const dataset = {
			type: "line",
			label: null,
			borderColor: "#" + color,
			borderWidth: 1,
			backgroundColor: "transparent",
			fill: false,
			data: lineExtensions,
			xAxisID: "x",
			yAxisID: yAxisId,
			tension: 0,
			pointRadius: 0,
			pointStyle: null,
			pointBorderWidth: 0,
			pointBackgroundColor: "transparent",
			min: Infinity,
			max: -Infinity,
			spanGaps: false,

			tooltip: {
				callbacks: {
					label: () => false,
				},
			},
		};
		return dataset;
	};

	const generateCalDataset = (
		parData,
		parCode,
		calType,
		valType,
		parUnit,
		type,
		color,
		symbol,
		rawPrec,
		timestamps,
		yAxisId = "y",
		lineWidth,
	) => {
		let lineData = Object.entries(parData).map(([key, value]) => ({
			x: Number(key) * 1000,
			y: value[0] === -999 ? null : value[0],
		}));
		let lineFlags = Object.entries(parData).map(([key, value]) => ({
			x: Number(key) * 1000,
			y: value[1],
		}));

		const dataset = {
			type: getDatasetType(type),
			label: `${parCode} ${calType} (${valType})`,
			parCode: parCode,
			calType: calType,
			valType: valType,
			borderColor: "#" + color,
			borderWidth: 0,
			backgroundColor: getDatasetBackgroundColor(type, color),
			fill: false,
			data: lineData,
			flags: lineFlags,
			xAxisID: "x",
			yAxisID: yAxisId,
			tension: 0,
			pointRadius: 2,
			pointStyle: "circle",
			pointBorderWidth: 1.2,
			pointBackgroundColor: "#" + color,
			categoryPercentage: 0.9,
			barPercentage: 0.9,
			min: getMin(lineData.map((point) => point.y)),
			max: getMax(lineData.map((point) => point.y)),

			tooltip: {
				callbacks: {
					label: (tooltipItem) => {
						const pCode = parCode.replace(/_VAL/, "");
						return `${pCode}/${calType}/${valType}/${parCode.match(/.*_VAL/) ? "VAL" : "RAW"}/${Number(tooltipItem.formattedValue).toFixed(rawPrec)}/${parUnit}/${ifExists(lineFlags[tooltipItem.dataIndex].y, "")}`;
					},
				},
			},

			datalabels: {
				display: showCalDataLabels,
				align: valType === "INDICATED" ? "left" : "right",
				offset: 4,
				clip: false,
				color: "#" + color.substring(0, 6),
				font: {
					size: 10,
				},
				formatter: (value, context) => {
					return value.y.toFixed(rawPrec);
				},
			},
		};
		return dataset;
	};

	const generatePercentDiffDataset = (isValidated, parameterList) => {
		if (!parameterList) return null;
		const actualPar = parameterList.find((par) => par.valType === "ACTUAL");

		const indicatedPar = parameterList.find(
			(par) => par.valType === "INDICATED",
		);

		if (!actualPar || !indicatedPar) {
			return null;
		}

		let lineData = [];
		let min = Infinity;
		let max = -Infinity;
		if (actualPar && indicatedPar) {
			const actualCalPar = [
				actualPar.parCode + (isValidated ? "_VAL" : ""),
				actualPar.calType,
				actualPar.valType,
			];
			const indicatedCalPar = [
				indicatedPar.parCode + (isValidated ? "_VAL" : ""),
				indicatedPar.calType,
				indicatedPar.valType,
			];
			const actualData = data[actualCalPar],
				indicatedData = data[indicatedCalPar];

			if (actualData && indicatedData) {
				lineData = Object.keys(actualData)
					.sort((a, b) => Number(a) - Number(b))
					.filter((key) => key !== "columnIndex")
					.map((timestamp) => {
						const actualValue = actualData[timestamp]
							? actualData[timestamp][0]
							: null;

						const indicatedValue = indicatedData[timestamp]
							? indicatedData[timestamp][0]
							: null;

						const pDiffVal =
							isNumeric(actualValue) &&
							isNumeric(indicatedValue) &&
							actualValue !== 0
								? Math.round(
										(10000 *
											(indicatedValue - actualValue)) /
											actualValue,
									) / 100
								: null;

						if (pDiffVal !== null) {
							if (pDiffVal < min) min = pDiffVal;
							if (pDiffVal > max) max = pDiffVal;
						}

						return {
							x: Number(timestamp) * 1000,
							y: pDiffVal,
						};
					});
			}
		}

		const dataset = {
			type: "line",
			label: "%Diff",
			borderColor: "#000000",
			borderWidth: 0,
			parCode: indicatedPar.parCode,
			calType: indicatedPar.calType,
			valType: "PDIFF",
			fill: false,
			data: lineData,
			xAxisID: "x",
			yAxisID: "y1",
			tension: 0,
			pointRadius: 1,
			pointBorderWidth: 1.2,
			pointBackgroundColor: "#000000",
			spanGaps: true,
			min: min,
			max: max,

			tooltip: {
				callbacks: {
					label: (tooltipItem) => {
						return `Diff///${isValidated ? "VAL" : "RAW"}/${tooltipItem.formattedValue}/${actualPar.unit}/`;
					},
				},
			},

			datalabels: {
				display: true,
				align: "top",
				offset: -18,
				clip: false,
				color: "#000000",
				font: {
					size: 10,
				},
				formatter: (value, context) => {
					return value.y.toFixed(2);
				},
			},
		};
		return dataset;
	};

	const generateDiffDataset = (isValidated, parameterList) => {
		if (!parameterList) return null;
		const actualPar = parameterList.find((par) => par.valType === "ACTUAL");

		const indicatedPar = parameterList.find(
			(par) => par.valType === "INDICATED",
		);

		if (!actualPar || !indicatedPar) {
			return null;
		}

		let lineData = [];
		let min = Infinity;
		let max = -Infinity;
		if (actualPar && indicatedPar) {
			const actualCalPar = [
				actualPar.parCode + (isValidated ? "_VAL" : ""),
				actualPar.calType,
				actualPar.valType,
			];
			const indicatedCalPar = [
				indicatedPar.parCode + (isValidated ? "_VAL" : ""),
				indicatedPar.calType,
				indicatedPar.valType,
			];
			const actualData = data[actualCalPar],
				indicatedData = data[indicatedCalPar];

			if (actualData && indicatedData) {
				lineData = Object.keys(actualData)
					.sort((a, b) => Number(a) - Number(b))
					.filter((key) => key !== "columnIndex")
					.map((timestamp) => {
						const diffVal =
							isNumeric(
								actualData[timestamp]
									? actualData[timestamp][0]
									: null,
							) &&
							isNumeric(
								indicatedData[timestamp]
									? indicatedData[timestamp][0]
									: null,
							)
								? indicatedData[timestamp][0] -
									actualData[timestamp][0]
								: null;
						if (diffVal > max) max = diffVal;
						if (diffVal < min) min = diffVal;
						return {
							x: timestamp * 1000,
							y: diffVal,
						};
					});
			}
		}

		const dataset = {
			type: "line",
			label: "Diff",
			borderColor: "#000000",
			borderWidth: 0,
			parCode: indicatedPar.parCode,
			calType: indicatedPar.calType,
			valType: "DIFF",
			fill: false,
			data: lineData,
			xAxisID: "x",
			yAxisID: "y1",
			tension: 0,
			pointRadius: 1,
			pointBorderWidth: 1.2,
			pointBackgroundColor: "#000000",
			spanGaps: true,
			min: min,
			max: max,

			tooltip: {
				callbacks: {
					label: (tooltipItem) => {
						return `Diff///${isValidated ? "VAL" : "RAW"}/${tooltipItem.formattedValue}/${actualPar.unit}/`;
					},
				},
			},

			datalabels: {
				display: true,
				align: "top",
				offset: -18,
				clip: false,
				color: "#000000",
				font: {
					size: 10,
				},
				formatter: (value, context) => {
					return value.y.toFixed(2);
				},
			},
		};

		return dataset;
	};

	const getMin = (data) => {
		const validValues = data.filter(
			(value) =>
				value !== null &&
				value !== undefined &&
				value !== -999 &&
				Number.isFinite(Number(value)),
		);

		if (!validValues.length) {
			return Infinity;
		}

		return Math.min(...validValues);
	};

	const getMax = (data) => {
		const validValues = data.filter(
			(value) =>
				value !== null &&
				value !== undefined &&
				value !== -999 &&
				Number.isFinite(Number(value)),
		);

		if (!validValues.length) {
			return -Infinity;
		}

		return Math.max(...validValues);
	};

	const isCalPlot = () => {
		if (!config) return false;
		return ["RAWCAL", "VALCAL", "VMCAL"].includes(config?.dataType || "");
	};

	return (
		<ValidationPlotContext.Provider
			value={{
				chartRefs: chartRefs.current,
				chartZooms: chartZooms.current,
				originalScaleMap: originalScales.current,
				zoomAll,
				zoomOthers,
				cursors: cursors.current,
				moveCursor,
				stepCursor,
				clearCursors,
				plugins,
				printable,
				isCalPlot,
			}}
		>
			<div
				id="validation-plot"
				className={
					"stackplot-wrapper" + (printable ? " printable" : "")
				}
				ref={stackPlotRef}
				style={{
					scale: (zoomLevel / 100).toString(),
					transformOrigin: "0 0",
					width: 10000 / zoomLevel + "%",
					height: 10000 / zoomLevel + "%",
				}}
			>
				<div className="stackplot-pages-container-wrapper">
					<div className="stackplot-pages-container">
						{generatePages()}
					</div>
					<StackPlotXAxis
						data={{
							datasets: [],
							labels: timestampLabels,
						}}
						xIndex={0}
					/>
				</div>
				<div
					className="stackplot-footnote"
					style={{ height: STACKPLOT_FOOTNOTE_HEIGHT }}
					hidden={printable || !config?.footnote}
				>
					{config?.footnote || ""}
				</div>
			</div>
		</ValidationPlotContext.Provider>
	);
}

function StackPlotChart({
	chartIndex,
	config,
	data,
	options,
	popoverCallback,
	chartSpacing = 20,
	chartHighlights,
	autoYAxis,
	fixedYScales,
}) {
	const [colorCoding, setColorCoding] = useState({});
	const chartRef = useRef(null);
	const originalScales = useRef(new Map());

	const {
		chartRefs,
		chartZooms,
		originalScaleMap,
		zoomAll,
		zoomOthers,
		cursors,
		moveCursor,
		stepCursor,
		clearCursors,
		plugins,
		printable,
		isCalPlot,
	} = useContext(ValidationPlotContext);

	useEffect(() => {
		originalScales.current.clear();
		chartZooms?.delete(chartIndex);
	}, [config]);

	useEffect(() => {
		originalScales.current.clear();
		if (chartZooms) {
			chartZooms.delete(chartIndex);
		}
		setColorCoding(getChartColorCoding());
	}, [data]);

	useEffect(() => {
		const chart = chartRef.current;

		if (!chart) {
			return;
		}

		chartRefs.set(chartIndex, chartRef);
		originalScaleMap.set(chartIndex, originalScales);

		if (chart.canvas) {
			chart.canvas.tabIndex = 0;
		}

		return () => {
			chartRefs.delete(chartIndex);
			originalScaleMap.delete(chartIndex);
		};
	}, [chartIndex, chartRefs, originalScaleMap, originalScales]);

	const getLeftMin = (includeZoom = true) => {
		if (!config?.parameters?.length) return 0;

		if (includeZoom && chartZooms?.get(chartIndex)?.y !== undefined) {
			return chartZooms.get(chartIndex).y.min;
		}

		if (isCalPlot()) {
			const chartPar = config.parameters.find(
				(par) => par.valType === "INDICATED",
			);

			if (chartPar) {
				const chartParameter = chartPar.parCode.split("-")[0];
				const chartCalType = chartPar.calType;

				if (!autoYAxis) {
					const fixedScale = fixedYScales.find(
						(scale) =>
							scale.parameter === chartParameter &&
							scale.calType === chartCalType,
					);

					if (fixedScale) {
						return fixedScale.min;
					}
				}
			}
		} else {
			const chartParameter = config.parameters[0].parCode.split("-")[0];

			if (!autoYAxis && fixedYScales[chartParameter]) {
				return fixedYScales[chartParameter].min;
			}
		}

		if (config.leftMin === undefined || config.leftMin === null) {
			if (config.parameters.length) {
				let min = Infinity;

				for (const dataset of data.datasets) {
					const parMin = dataset.min;

					if (
						dataset.yAxisID === "y" &&
						Number.isFinite(parMin) &&
						parMin < min
					) {
						min = parMin;
					}
				}

				return Number.isFinite(min) ? min : 0;
			}

			return 0;
		}

		return config.leftMin;
	};

	const getLeftMax = (includeZoom = true) => {
		if (!config?.parameters?.length) return 0;

		if (includeZoom && chartZooms?.get(chartIndex)?.y !== undefined) {
			return chartZooms.get(chartIndex).y.max;
		}

		if (isCalPlot()) {
			const chartPar = config.parameters.find(
				(par) => par.valType === "INDICATED",
			);

			if (chartPar) {
				const chartParameter = chartPar.parCode.split("-")[0];
				const chartCalType = chartPar.calType;

				if (!autoYAxis) {
					const fixedScale = fixedYScales.find(
						(scale) =>
							scale.parameter === chartParameter &&
							scale.calType === chartCalType,
					);

					if (fixedScale) {
						return fixedScale.max;
					}
				}
			}
		} else {
			const chartParameter = config.parameters[0].parCode.split("-")[0];

			if (!autoYAxis && fixedYScales[chartParameter]) {
				return fixedYScales[chartParameter].max;
			}
		}

		if (config.leftMax === undefined || config.leftMax === null) {
			if (config.parameters.length) {
				let max = -Infinity;

				for (const dataset of data.datasets) {
					const parMax = dataset.max;

					if (
						dataset.yAxisID === "y" &&
						Number.isFinite(parMax) &&
						parMax > max
					) {
						max = parMax;
					}
				}

				return Number.isFinite(max) ? max : 1;
			}

			return 0;
		}

		return config.leftMax;
	};

	const getRightMin = (includeZoom = true) => {
		if (includeZoom && chartZooms?.get(chartIndex)?.y1 !== undefined) {
			return chartZooms.get(chartIndex).y1.min;
		}

		if (config.rightMin === undefined || config.rightMin === null) {
			const rightPars = config.parameters.filter(
				(parameter) => parameter.side === "R",
			);

			if (rightPars.length) {
				let min = Infinity;

				for (const dataset of data.datasets) {
					if (
						dataset.yAxisID === "y1" &&
						Number.isFinite(dataset.min) &&
						dataset.min < min
					) {
						min = dataset.min;
					}
				}

				return Number.isFinite(min) ? min : 0;
			}

			return 0;
		}

		return config.rightMin;
	};

	const getRightMax = (includeZoom = true) => {
		if (includeZoom && chartZooms?.get(chartIndex)?.y1 !== undefined) {
			return chartZooms.get(chartIndex).y1.max;
		}

		if (config.rightMax === undefined || config.rightMax === null) {
			const rightPars = config.parameters.filter(
				(parameter) => parameter.side === "R",
			);

			if (rightPars.length) {
				let max = -Infinity;

				for (const dataset of data.datasets) {
					if (
						dataset.yAxisID === "y1" &&
						Number.isFinite(dataset.max) &&
						dataset.max > max
					) {
						max = dataset.max;
					}
				}

				return (
					(Number.isFinite(max) ? max : 0) + (isCalPlot() ? 80 : 0)
				);
			}

			return isCalPlot() ? 80 : 0;
		}

		return config.rightMax + (isCalPlot() ? 80 : 0);
	};

	function zoomDelta(scale, zoom, center) {
		const range = scale.max - scale.min;
		const newRange = range * (zoom - 1);

		const centerPoint = scale.isHorizontal() ? center.x : center.y;
		// `scale.getValueForPixel()` can return a value less than the `scale.min` or
		// greater than `scale.max` when `centerPoint` is outside chartArea.
		const minPercent = Math.max(
			0,
			Math.min(
				1,
				(scale.getValueForPixel(centerPoint) - scale.min) / range || 0,
			),
		);

		const maxPercent = 1 - minPercent;

		return {
			min: newRange * minPercent,
			max: newRange * maxPercent,
		};
	}

	function updateRange(scale, { min, max }, limits, zoom = false) {
		const { id, axis, options: scaleOpts } = scale;

		const scaleLimits = limits?.get(id) || {};
		const { minRange = 0 } = scaleLimits;
		const minLimit =
			!scale.isHorizontal() || scaleLimits["min"] === undefined
				? -Infinity
				: scaleLimits["min"];
		const maxLimit =
			!scale.isHorizontal() || scaleLimits["max"] === undefined
				? Infinity
				: scaleLimits["max"];

		const range = zoom
			? Math.max(max - min, minRange)
			: scale.max - scale.min;
		const offset = (range - max + min) / 2;
		min -= offset;
		max += offset;

		if (min < minLimit) {
			min = minLimit;
			max = Math.min(minLimit + range, maxLimit);
		} else if (max > maxLimit) {
			max = maxLimit;
			min = Math.max(maxLimit - range, minLimit);
		}
		scaleOpts.min = min;
		scaleOpts.max = max;

		// return true if the scale range is changed
		return scale.parse(min) !== scale.min || scale.parse(max) !== scale.max;
	}

	function zoomNumericalScale(scale, zoom, center, limits) {
		const delta = zoomDelta(scale, zoom, center);
		const newRange = {
			min: scale.min + delta.min,
			max: scale.max - delta.max,
		};
		return updateRange(scale, newRange, limits, true);
	}

	const doZoom = (chart, x, y, point) => {
		const xEnabled = x !== 1;
		const yEnabled = y !== 1;

		for (let scaleId in chart.scales) {
			const scale = chart.scales[scaleId];
			if (scale.isHorizontal() && xEnabled) {
				zoomNumericalScale(scale, x, point, originalScales.current);
			} else if (!scale.isHorizontal() && yEnabled) {
				if (isCalPlot() && scaleId === "y1") {
					continue;
				}
				zoomNumericalScale(scale, y, point, originalScales.current);
			}
		}

		chart.update("none");
		const xMin = chart.scales.x.min;
		const xMax = chart.scales.x.max;
		zoomOthers(chartIndex, xMin, xMax);
	};

	const getColorForCode = (valCode, loggerFlag) => {
		if (!valCode) return null;
		if (GREEN_CODES.includes(valCode)) {
			return "green";
		} else {
			return "red";
		}
	};

	const getCalChartColorCoding = () => {
		const colorCoding = {};
		if (!data?.datasets?.length) return colorCoding;
		let interval = 24 * 60 * 60 * 1000;
		const colorLists = {
			green: [],
			yellow: [],
			red: [],
		};
		for (const dataset of data.datasets) {
			if (dataset.label?.match(".*_VAL")) {
				const lineData = dataset.data;
				const lineFlags = dataset.flags;
				for (let i = 0; i < lineFlags.length; i++) {
					if (lineFlags[i]) {
						const color = getColorForCode(lineFlags[i].y, null);
						if (color) {
							colorLists[color].push(
								moment(lineData[i].x)
									.hour(0)
									.minute(0)
									.valueOf(),
							);
						}
					}
				}
			}
		}
		for (let color in colorLists) {
			const colorTimestamps = colorLists[color];
			if (!colorTimestamps?.length) continue;
			colorTimestamps.sort((a, b) => Number(a) - Number(b));
			const colorRanges = [];
			for (let i of colorTimestamps) {
				colorRanges.push([i, i + interval]);
			}

			for (let i = 0; i < colorRanges.length; i++) {
				const range = colorRanges[i];

				colorCoding[`${color}-${i}`] = {
					type: "box",
					xMin: range[0],
					xMax: range[1],
					yMin: -Infinity,
					yMax: Infinity,
					borderWidth: 0,
					backgroundColor: colors[`ars-${color}-600`] + "40",
				};
			}
		}
		return colorCoding;
	};

	const getChartColorCoding = () => {
		if (isCalPlot()) return getCalChartColorCoding();
		const colorCoding = {};
		if (!data?.datasets?.length) return colorCoding;
		let interval;
		for (let dataset of data.datasets) {
			if (dataset.data?.length > 1) {
				interval = dataset.data[1].x - dataset.data[0].x;
				break;
			}
		}
		if (!interval) return colorCoding;
		const colorLists = {
			green: [],
			yellow: [],
			red: [],
		};
		for (const dataset of data.datasets) {
			if (dataset.label?.match(".*_VAL")) {
				const rawDataset = data.datasets.find(
					(ds) =>
						ds.label ===
						dataset.label.substr(0, dataset.label.length - 4),
				);
				if (!rawDataset) continue;
				const rawFlags = rawDataset.flags;
				const lineData = dataset.data;
				const lineFlags = dataset.flags;
				for (let i = 0; i < lineFlags.length; i++) {
					if (lineFlags[i]) {
						const color = getColorForCode(
							lineFlags[i],
							rawFlags[i],
						);
						if (color) {
							colorLists[color].push(lineData[i].x);
						}
					}
				}
			}
		}
		for (let color in colorLists) {
			const colorTimestamps = colorLists[color];
			if (!colorTimestamps?.length) continue;
			colorTimestamps.sort((a, b) => Number(a) - Number(b));
			let l = 0,
				r;
			const colorRanges = [];
			for (let i = 1; i < colorTimestamps.length; i++) {
				if (colorTimestamps[i] - colorTimestamps[i - 1] !== interval) {
					r = i - 1;
					colorRanges.push([colorTimestamps[l], colorTimestamps[r]]);
					l = i;
				}
			}
			colorRanges.push([
				colorTimestamps[l],
				colorTimestamps[colorTimestamps.length - 1],
			]);

			for (let i = 0; i < colorRanges.length; i++) {
				const range = colorRanges[i];

				colorCoding[`${color}-${i}`] = {
					type: "box",
					xMin: range[0],
					xMax: range[1] + interval,
					yMin: -Infinity,
					yMax: Infinity,
					borderWidth: 0,
					backgroundColor: colors[`ars-${color}-600`] + "40",
				};
			}
		}
		return colorCoding;
	};

	const buildOptions = () => {
		let newOptions = JSON.parse(JSON.stringify(options));
		newOptions.chartIndex = chartIndex;
		newOptions.clip = true;

		newOptions.scales.y.afterFit = (axis) => {
			axis.width = Y_AXIS_LABEL_WIDTH;
		};

		if (chartZooms?.get("x")) {
			newOptions.scales.x.min = chartZooms.get("x").min;
			newOptions.scales.x.max = chartZooms.get("x").max;
		} else {
			const labels = data.labels;
			const minTs = moment(labels[0]).valueOf(),
				maxTs = moment(labels[labels.length - 1]).valueOf();

			newOptions.scales.x.min = minTs;
			newOptions.scales.x.max = maxTs;
		}
		if (isCalPlot()) {
			newOptions.scales.x.time.minUnit = "day";
		}

		const originalY = {
			min: getLeftMin(false),
			max: getLeftMax(false),
		};

		const currentY = chartZooms?.get(chartIndex)?.y || originalY;

		newOptions.scales.y.min = currentY.min;
		newOptions.scales.y.max = currentY.max;
		newOptions.scales.y.originalMin = originalY.min;
		newOptions.scales.y.originalMax = originalY.max;

		originalScales.current.set("y", originalY);

		if (config.hasRightSide) {
			const originalY1 = {
				min: getRightMin(false),
				max: getRightMax(false),
			};

			const currentY1 = chartZooms?.get(chartIndex)?.y1 || originalY1;

			newOptions.scales.y1 = {
				type: "linear",
				position: "right",
				display: true,
				ticks: {
					font: STACKPLOT_AXIS_FONT,
				},
				suggestedMin: 0,
				suggestedMax: 1,
				min: currentY1.min,
				max: currentY1.max,
				originalMin: originalY1.min,
				originalMax: originalY1.max,
				grid: {
					drawOnChartArea: false,
				},
				afterFit: (axis) => {
					axis.width = Y_AXIS_LABEL_WIDTH;
				},
			};

			originalScales.current.set("y1", originalY1);
			newOptions.layout.padding.right = 0;
		} else {
			originalScales.current.delete("y1");
		}

		newOptions.plugins.tooltip.enabled = false;
		newOptions.plugins.tooltip.external = externalTooltipHandler;
		newOptions.plugins.tooltip.callbacks = {};
		newOptions.plugins.tooltip.callbacks.title = (tooltipItems) => {
			const timestamp = moment(
				tooltipItems[0].label,
				"MMM D, YYYY, HH:mm:ss a",
			).seconds(0);
			return timestamp.format("MMM D, YYYY, HH:mm");
		};

		if (newOptions.scales.x && !originalScales.current.get("x")) {
			originalScales.current.set("x", {
				min: newOptions.scales.x.min,
				max: newOptions.scales.x.max,
			});
		}

		newOptions.originalScales = Object.fromEntries(originalScales.current);

		newOptions.plugins.zoom.pan.onPan = ({ chart }) => {
			if (chart.options.plugins.zoom.pan.mode === "x") {
				const xMin = chart.scales.x.min;
				const xMax = chart.scales.x.max;

				zoomOthers(chartIndex, xMin, xMax);
			}
		};

		newOptions.plugins.zoom.zoom.onZoomStart = ({
			chart,
			event,
			point,
		}) => {
			if (chart.scales.x && !originalScales.current.get("x")) {
				originalScales.current.set("x", {
					min: chart.scales.x.min,
					max: chart.scales.x.max,
				});
			}
			if (chart.scales.y && !originalScales.current.get("y")) {
				originalScales.current.set("y", {
					min: chart.scales.y.min,
					max: chart.scales.y.max,
				});
			}
			if (chart.scales.y1 && !originalScales.current.get("y1")) {
				originalScales.current.set("y1", {
					min: chart.scales.y1.min,
					max: chart.scales.y1.max,
				});
			}
			chart.originalScales = Object.fromEntries(originalScales.current);
			if (event.ctrlKey) {
				event.preventDefault();
				const speed =
					1 +
					(event.deltaY >= 0
						? -chart.options.plugins.zoom.zoom.wheel.speed
						: chart.options.plugins.zoom.zoom.wheel.speed);
				doZoom(chart, speed, 1, point);
				return false;
			} else if (event.shiftKey) {
				event.preventDefault();
				const speed =
					1 +
					(event.deltaY >= 0
						? -chart.options.plugins.zoom.zoom.wheel.speed
						: chart.options.plugins.zoom.zoom.wheel.speed);
				doZoom(chart, 1, speed, point);
				chartZooms.set(chartIndex, {
					y: chart.scales.y
						? {
								min: chart.scales.y.min,
								max: chart.scales.y.max,
							}
						: null,
					y1: chart.scales.y1
						? {
								min: chart.scales.y1.min,
								max: chart.scales.y1.max,
							}
						: null,
				});
				return false;
			} else {
				return false;
			}
		};

		newOptions.plugins.zoom.pan.onPanStart = ({ chart, event }) => {
			if (chart.scales.x && !originalScales.current.get("x")) {
				originalScales.current.set("x", {
					min: chart.scales.x.min,
					max: chart.scales.x.max,
				});
			}
			if (chart.scales.y && !originalScales.current.get("y")) {
				originalScales.current.set("y", {
					min: chart.scales.y.min,
					max: chart.scales.y.max,
				});
			}
			if (chart.scales.y1 && !originalScales.current.get("y1")) {
				originalScales.current.set("y1", {
					min: chart.scales.y1.min,
					max: chart.scales.y1.max,
				});
			}
			chart.originalScales = Object.fromEntries(originalScales.current);
			chart.isCalPlot = isCalPlot();
			if (event.srcEvent.shiftKey) {
				chart.options.plugins.zoom.pan.mode = "y";
				event.deltaX = 0;
			} else {
				chart.options.plugins.zoom.pan.mode = "x";
				event.deltaY = 0;
			}
		};

		newOptions.plugins.annotation.annotations = {
			...newOptions.plugins.annotation.annotations,
			...chartHighlights,
			...colorCoding,
		};

		newOptions.plugins.datalabels = { display: false };

		if (printable) {
			newOptions.plugins.customCanvasBackgroundColor = {
				color: "white",
			};
		}

		if (isCalPlot()) {
			newOptions.plugins.decimation = { enabled: false };
			if (newOptions.scales.y1) {
				newOptions.scales.y1.display = false;
				newOptions.plugins.zoom.limits.y1 = {
					min: "original",
					max: "original",
				};
			}
		}

		return newOptions;
	};

	const getLeftLegend = () => {
		if (!config?.parameters) return null;
		const leftParameters = config.parameters.filter(
			(parameter) => parameter.side === "L",
		);
		const legendItems = [];
		for (const par of leftParameters) {
			const legendItem = (
				<span
					style={{ color: "#" + par.color }}
				>{`${par.parCode} (${par.unit})`}</span>
			);
			legendItems.push(legendItem);
		}
		return legendItems;
	};

	const getCenterLegend = () => {
		if (!isCalPlot() || !config?.parameters?.length) return null;
		let calPar;
		for (let par of config.parameters) {
			if (par.valType === "INDICATED") {
				calPar = par;
				break;
			}
		}
		if (!calPar) return null;
		const parameter = calPar.parCode,
			calType = calPar.calType;
		return `${parameter} ${calType}`;
	};

	const getRightLegend = () => {
		if (!config?.parameters?.length || !config?.hasRightSide) return null;
		const rightParameters = config.parameters.filter(
			(parameter) => parameter.side === "R",
		);
		const legendItems = [];
		for (const par of rightParameters) {
			const legendItem = (
				<span style={{ color: "#" + par.color }}>{`${par.parCode} (${
					par.valType === "DIFF" ? getDiffUnit(par) : par.unit
				})`}</span>
			);
			legendItems.push(legendItem);
		}
		return legendItems;
	};

	const getDiffUnit = (par) => {
		if (!config?.parameters?.length) return null;
		const indicatedPar = config.parameters.find(
			(parameter) => parameter.valType === "INDICATED",
		);
		return indicatedPar?.unit || null;
	};

	const dblClickPlugin = {
		id: "doubleClickZoomReset",
		defaults: {
			events: ["dblclick"],
		},
		beforeEvent: (chart, args) => {
			const event = args.event;

			if (event.type !== "dblclick") {
				return false;
			}

			if (!chart.originalScales) {
				return false;
			}

			if (event.native.shiftKey) {
				const savedZoom = chartZooms?.get(chartIndex);

				if (savedZoom) {
					const {
						y: ignoredY,
						y1: ignoredY1,
						...remainingZoom
					} = savedZoom;

					if (Object.keys(remainingZoom).length) {
						chartZooms.set(chartIndex, remainingZoom);
					} else {
						chartZooms.delete(chartIndex);
					}
				}

				const originalY = originalScales.current.get("y");

				const originalY1 = originalScales.current.get("y1");

				if (chart.scales.y && originalY) {
					chart.zoomScale("y", originalY);
				}

				if (chart.scales.y1 && originalY1) {
					chart.zoomScale("y1", originalY1);
				}

				chart.originalScales = Object.fromEntries(
					originalScales.current,
				);

				chart.update("none");
			} else if (event.native.ctrlKey) {
				if (chart.originalScales.x) {
					zoomAll(
						chart.originalScales.x.min,
						chart.originalScales.x.max,
					);
				}
			}

			return false;
		},
	};

	const xCursorPlugin = {
		id: "xCursorPlugin",
		defaults: {
			events: ["keydown", "click", "blur"],
		},
		beforeEvent: (chart, args) => {
			const event = args.event.native;
			const type = args.event.type;
			const ctrl = event.ctrlKey;
			const cursorId = ctrl ? "cursor2" : "cursor1";
			if (type === "keydown") {
				if (event.shiftKey) return;
				const key = event.code;
				const xIndex =
					chart.options.plugins.annotation.annotations[cursorId]
						?.index || null;
				if (key === "ArrowLeft") {
					stepCursor(chartIndex, cursorId, -1);
				} else if (key === "ArrowRight") {
					stepCursor(chartIndex, cursorId, 1);
				}
			} else if (type === "click") {
				if (event.detail > 1) return;
				if (event.shiftKey) {
					clearCursors();
					return;
				}
				chart.canvas.focus();
				if (event.detail > 1) return;
				const position = getRelativePosition(event, chart);
				const xCoord = chart.scales.x.getValueForPixel(position.x);

				const dataTimestamps = chart.scales.x.getDataTimestamps();
				let x = null;
				let index = null;

				if (isCalPlot()) {
					/*
					 * Calibration cursors are day-based. Place the cursor at the
					 * beginning of the calendar day containing the click, regardless
					 * of whether that day contains a calibration data point.
					 */
					x = moment(xCoord).startOf("day").valueOf();

					/*
					 * Preserve an appropriate dataset index for keyboard cursor
					 * navigation. Use the most recent data timestamp at or before
					 * the clicked position.
					 */
					for (let i = 0; i < dataTimestamps.length; i++) {
						if (dataTimestamps[i] > xCoord) {
							index = Math.max(0, i - 1);
							break;
						}
					}

					if (index === null && dataTimestamps.length) {
						index = dataTimestamps.length - 1;
					}
				} else {
					for (let i = 1; i < dataTimestamps.length; i++) {
						if (dataTimestamps[i] > xCoord) {
							x = dataTimestamps[i - 1];
							index = i - 1;
							break;
						}
					}

					if (
						x === null &&
						dataTimestamps.length &&
						xCoord >= dataTimestamps[dataTimestamps.length - 1]
					) {
						x = dataTimestamps[dataTimestamps.length - 1];
						index = dataTimestamps.length - 1;
					}
				}

				if (x !== null) {
					moveCursor(chartIndex, cursorId, x, index);
				} else {
					chart.options.plugins.annotation = undefined;
				}

				chart.update("none");
			}
		},
	};

	const originalLimitsPlugin = {
		id: "originalLimitsPlugin",
		afterUpdate: (chart, args, options) => {
			if (chart.scales.x && !originalScales.current.get("x")) {
				originalScales.current.set("x", {
					min: chart.scales.x.min,
					max: chart.scales.x.max,
				});
			}
			if (chart.scales.y && !originalScales.current.get("y")) {
				originalScales.current.set("y", {
					min: chart.scales.y.min,
					max: chart.scales.y.max,
				});
			}
			if (chart.scales.y1 && !originalScales.current.get("y1")) {
				originalScales.current.set("y1", {
					min: chart.scales.y1.min,
					max: chart.scales.y1.max,
				});
			}
			chart.originalScales = Object.fromEntries(originalScales.current);
		},
	};

	const getPlugins = () => {
		return [
			dblClickPlugin,
			xCursorPlugin,
			backgroundColorPlugin,
			dataLabelPlugin,
			originalLimitsPlugin,
			...plugins,
		];
	};

	const chartHeight = config.chartHeight || 200;

	return (
		<div
			id={"stackplot-chart-" + chartIndex}
			className="stackplot-chart-wrapper"
			style={{
				height: `${chartHeight}px`,
				minHeight: `${chartHeight}px`,
				flexShrink: 0,
			}}
		>
			<div
				className="validation-chart-legend-left"
				style={{
					height: `${chartHeight}px`,
					maxHeight: `${chartHeight}px`,
				}}
			>
				{getCenterLegend()}
				{getLeftLegend()}
			</div>

			<Chart
				type="line"
				data={data}
				options={buildOptions()}
				ref={chartRef}
				plugins={getPlugins()}
				style={{
					padding: "0 80px",
					height: `${chartHeight}px`,
					minHeight: `${chartHeight}px`,
					maxHeight: `${chartHeight}px`,
					flexShrink: 0,
				}}
				onContextMenu={popoverCallback}
			/>

			<div
				className="validation-chart-legend-right"
				style={{
					height: `${chartHeight}px`,
					maxHeight: `${chartHeight}px`,
				}}
			>
				{getRightLegend()}
			</div>
		</div>
	);
}

function StackPlotXAxis({ data, xIndex, bufferSize = 0 }) {
	const chartRef = useRef(null);

	const { chartRefs, chartZooms, printable } = useContext(
		ValidationPlotContext,
	);

	useEffect(() => {
		const chartId = `x-${xIndex}`;

		if (chartRef.current) {
			chartRefs.set(chartId, chartRef);
		}

		return () => {
			chartRefs.delete(chartId);
		};
	}, [chartRefs, xIndex]);

	const getOptions = () => {
		const newOptions = {
			...xAxisOptions,
			scales: {
				...xAxisOptions.scales,
				x: {
					...xAxisOptions.scales.x,
				},
				y: {
					...xAxisOptions.scales.y,
				},
			},
			plugins: {
				...xAxisOptions.plugins,
			},
		};

		if (chartZooms?.get("x")) {
			const xZoom = chartZooms.get("x");

			newOptions.scales.x.min = xZoom.min;
			newOptions.scales.x.max = xZoom.max;
		} else if (data.labels?.length) {
			newOptions.scales.x.min = moment(data.labels[0]).valueOf();

			newOptions.scales.x.max = moment(
				data.labels[data.labels.length - 1],
			).valueOf();
		}

		newOptions.plugins.customCanvasBackgroundColor = {
			color: printable ? "white" : "transparent",
		};

		return newOptions;
	};

	return (
		<Chart
			className="stackplot-x-axis"
			data={data}
			options={getOptions()}
			ref={chartRef}
			plugins={[backgroundColorPlugin]}
			style={{
				padding: "0 80px",
				maxHeight: STACKPLOT_X_AXIS_HEIGHT + "px",
				marginBottom: printable ? bufferSize + "px" : 0,
			}}
		/>
	);
}

function StackPlotPage({ title, footnote, pageNumber, charts, numPages }) {
	const { printable } = useContext(ValidationPlotContext);

	const getTitle = () => {
		return title || "";
	};

	const isFirstPage = () => {
		return pageNumber === 1;
	};

	const getCharts = () => {
		return charts || [];
	};

	const getFootnote = () => {
		return footnote || "";
	};

	const getDate = () => {
		return moment().format("MMM D, YYYY");
	};

	return (
		<div className={"stackplot-page" + (printable ? " printable" : "")}>
			<div className="stackplot-page-header">{`${title} (Page ${pageNumber} of ${numPages})`}</div>
			<div
				className="stackplot-title"
				style={{ height: STACKPLOT_TITLE_HEIGHT }}
				hidden={!(printable && isFirstPage())}
			>
				{getTitle()}
			</div>
			{getCharts()}
			<div
				className="stackplot-footnote"
				style={{ height: STACKPLOT_FOOTNOTE_HEIGHT }}
				hidden={!(printable && footnote)}
			>
				{getFootnote()}
			</div>
			<div className="stackplot-page-footer">{`${getDate()}`}</div>
		</div>
	);
}

export default ValidationPlot;
